// components/sections/index.tsx
"use client";
import React from "react";
import styled from "styled-components";
import type {
  HeroSection as HeroData,
  OverviewSection as OverviewData,
  FeatureSection,
  TechnicalSpecsSection as TechnicalSpecsData,
  HowItWorksSection as HowItWorksData,
  PricingSection as PricingData,
  SecuritySection as SecurityData,
  UseCaseSection,
  GettingStartedSection as GettingStartedData,
  RoadmapSection as RoadmapData,
  EcosystemSection as EcosystemData,
  CTASection as CTAData,
  ProductMeta,
} from "../../_sys/_types";
import HeroGradientSection from "@/components/sections/HeroGradientSection";
import Image from "next/image";
import PrettyTextFade from "@/components/functional/PrettyTextFade";
import theme from "@/globals/theme";
import { BentoBlock } from "@/components/functional/BentoBlock/BentoBlock";

import {
  BlockRenderer,
  RenderTable,
  RenderList,
} from "../blocks/BlockRenderer";
import { StepTracker } from "../primitives/StatusAndProgress";
import { PricingTierGrid } from "../primitives/PricingTierGrid";
import { BadgeRow } from "../primitives/DataDisplay";

import { getIcon } from "../blocks/shared";
import { MockUpFrame } from "../primitives/MockUpFrame";
// import { Scene3D } from "../primitives/Scene3D";

// ============================================================
// SHARED STYLED COMPONENTS
// ============================================================

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 24px 0;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionSubtitle = styled.h3`
  font-size: 1.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 48px 0 16px 0;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin: 32px 0 12px 0;
  }
`;

const Paragraph = styled.p`
  font-size: 1.125rem;
  line-height: 1.8;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0 0 20px 0;

  strong {
    color: ${({ theme }) => theme.colors.textPrimary};
    font-weight: 600;
  }

  em {
    font-style: italic;
  }
`;

const Button = styled.a<{ variant?: "primary" | "secondary" | "tertiary" }>`
  display: inline-block;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid ${({ theme }) => theme.colors.semitransparent}
  color: ${({ theme }) => theme.colors.textPrimary};

  ${({ variant, theme }) => {
    if (variant === "primary") {
      return `
        background: ${theme.gradients.strong};
        color: ${theme.colors.textPrimary};
        box-shadow: 0 4px 15px rgba(234, 188, 102, 0.3);

        &:hover {
          background: ${theme.colors.primary};
          box-shadow: 0 6px 20px rgba(234, 192, 102, 0.4);
          transform: translateY(-2px);
          color: ${theme.colors.primaryBackground};
        }
      `;
    } else if (variant === "secondary") {
      return `
        background: ${theme.colors.secondaryBackground};
        color: ${theme.colors.textPrimary};
        border-color: ${theme.gradients.primary};

        &:hover {
          background: ${theme.colors.primaryBackground};
          transform: translateY(-2px);
        }
      `;
    } else {
      return `
        background: ${theme.gradients.glass};
        color: ${theme.colors.textPrimary};
        border: 1px solid ${theme.colors.secondaryBackground};

        &:hover {
          border-color: ${theme.colors.secondaryBackground};
          background: ${theme.colors.primaryBackground};
          transform: translateY(-2px);
        }
      `;
    }
  }}
`;

const HighlightIcon = styled.div`
  font-size: 2rem;
  flex-shrink: 0;

  display: flex;
  padding: 5px 15px;
`;

const HighlightContent = styled.div`
  flex: 1;
  padding: 0px 15px;
`;

const HighlightLabel = styled.h4`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 4px 0;
`;

const HighlightDescription = styled.p`
  font-size: 0.9375rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
  line-height: 1.6;
`;

const Note = styled.div`
  background: ${({ theme }) => theme.colors.primaryBackground};
  border-left: 4px solid ${({ theme }) => theme.colors.primary};
  padding: 16px 20px;
  margin: 24px 0;
  border-radius: 4px;

  strong {
    color: ${({ theme }) => theme.colors.primary};
  }

  p {
    margin: 0;
    font-size: 1rem;
  }
`;

const Grid = styled.div<{ columns?: number }>`
  display: grid;
  grid-template-columns: repeat(${(props) => props.columns || 1}, 1fr);
  gap: 24px;
  margin: 32px 0;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(
      ${(props) => Math.min(props.columns || 1, 2)},
      1fr
    );
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div<{ background?: string }>`
  background: ${({ theme, background }) =>
    background || theme.colors.primaryBackground};
  padding: 28px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

// ============================================================
// HERO SECTION
// ============================================================

const HeroContainer = styled.div`
  position: relative;
  text-align: center;
  overflow: hidden;
`;

const HeroInner = styled.div<{ $hasMockup: boolean }>`
  position: relative;
  z-index: ${({ theme }) => theme.zIndex.root};
  display: ${({ $hasMockup }) => ($hasMockup ? "grid" : "block")};
  grid-template-columns: ${({ $hasMockup }) =>
    $hasMockup ? "1fr 1fr" : "1fr"};
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xlarge};
  text-align: ${({ $hasMockup }) => ($hasMockup ? "left" : "center")};

  @media (max-width: ${({ theme }) => theme.breakpoints.laptop}) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const HeroCopyColumn = styled.div``;

const HeroMockupColumn = styled.div`
  display: flex;
  justify-content: center;

  /* MockUpFrame owns its own top/bottom margin for standalone use —
     zero it out here since it's living inside the hero grid instead. */
  & > div {
    margin: 0;
  }
`;

const HeroSubtitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 600;
  margin: 0 0 20px 0;
  opacity: 0.95;
  color: ${({ theme }) => theme.colors.static.white};

  @media (max-width: 768px) {
    font-size: 1.375rem;
  }
`;

const HeroDescription = styled.p<{ $hasMockup: boolean }>`
  font-size: 1.25rem;
  margin: 0 0 32px 0;
  opacity: 0.8;
  max-width: ${({ $hasMockup }) => ($hasMockup ? "none" : "800px")};
  margin-left: ${({ $hasMockup }) => ($hasMockup ? "0" : "auto")};
  margin-right: ${({ $hasMockup }) => ($hasMockup ? "0" : "auto")};
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.static.white};

  @media (max-width: 768px) {
    font-size: 1.0625rem;
  }
`;

const HeroButtons = styled.div<{ $hasMockup: boolean }>`
  display: flex;
  gap: 16px;
  justify-content: ${({ $hasMockup }) =>
    $hasMockup ? "flex-start" : "center"};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing.large};

  @media (max-width: ${({ theme }) => theme.breakpoints.laptop}) {
    justify-content: center;
  }
`;

const HeroBadgeWrap = styled.div`
  max-width: 420px;

  @media (max-width: ${({ theme }) => theme.breakpoints.laptop}) {
    max-width: none;
    margin: 0 auto;
  }
`;

const HeroThumb = styled(Image)`
  user-select: none;

  @media (max-width: 768px) {
    transform: scale(0.7);
  }
`;

export const HeroSection: React.FC<{
  data: HeroData;
  metadata: ProductMeta;
}> = ({ data, metadata }) => {
  const hasMockup = Boolean(data.mockup);

  return (
    <HeroContainer>
      <HeroGradientSection>
        {/* {data.scene3d && <Scene3D block={data.scene3d} />} */}
        <HeroInner $hasMockup={hasMockup}>
          <HeroCopyColumn>
            <HeroThumb
              src={metadata.photo.thumbnail}
              alt={`${metadata.productName} Thumbnail`}
              height={hasMockup ? 72 : 150}
              width={hasMockup ? 72 : 150}
              priority
            />

            <PrettyTextFade
              color={theme.colors.primaryBackground}
              size={48}
              speedFade={1200}
              speedWave={3600}
              waveStrength={20}
              mode="words"
            >
              {data.title}
            </PrettyTextFade>

            <HeroSubtitle>{data.subtitle}</HeroSubtitle>
            <HeroDescription $hasMockup={hasMockup}>
              {data.description}
            </HeroDescription>
            <HeroButtons $hasMockup={hasMockup}>
              {data.ctaButtons.map((button, idx) => (
                <Button
                  key={idx}
                  href={button.url || "#"}
                  variant={button.variant}
                  target={button.external ? "_blank" : undefined}
                  rel={button.external ? "noopener noreferrer" : undefined}
                >
                  {button.label}
                </Button>
              ))}
            </HeroButtons>
            {data.badges && data.badges.length > 0 && (
              <HeroBadgeWrap>
                <BadgeRow badges={data.badges} columns={1} />
              </HeroBadgeWrap>
            )}
          </HeroCopyColumn>

          {data.mockup && (
            <HeroMockupColumn>
              <MockUpFrame block={data.mockup} />
            </HeroMockupColumn>
          )}
        </HeroInner>
      </HeroGradientSection>
    </HeroContainer>
  );
};

// ============================================================
// OVERVIEW SECTION
// ============================================================

export const OverviewSection: React.FC<{ data: OverviewData }> = ({ data }) => {
  return (
    <div>
      <SectionTitle>{data.title}</SectionTitle>
      {data.paragraphs.map((paragraph, idx) => (
        <Paragraph
          key={idx}
          dangerouslySetInnerHTML={{
            __html: paragraph.replace(/\*([^*]+)\*/g, "<em>$1</em>"),
          }}
        />
      ))}
      {data.highlights && data.highlights.length > 0 && (
        <Grid columns={3}>
          {data.highlights.map((highlight, idx) => {
            const Icon = getIcon(highlight?.icon);

            return (
              <BentoBlock key={idx}>
                <HighlightIcon>
                  <Icon size={36} />
                </HighlightIcon>
                <HighlightContent>
                  <HighlightLabel>{highlight.label}</HighlightLabel>
                  <HighlightDescription>
                    {highlight.description}
                  </HighlightDescription>
                </HighlightContent>
              </BentoBlock>
            );
          })}
        </Grid>
      )}
      <BlockRenderer blocks={data.blocks} />
    </div>
  );
};

// ============================================================
// FEATURES SECTION
// ============================================================

const FeatureCard = styled(Card)`
  scroll-margin-top: 100px;
`;

const FeatureHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const FeatureIconBadge = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borders.radius.rounded};
  background: ${({ theme }) => theme.gradients.strong};
  color: ${({ theme }) => theme.colors.static.white};
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
`;

const FeatureDescription = styled.p`
  font-size: 1.0625rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0 0 16px 0;
  line-height: 1.7;
`;

const FeatureListColumn = styled.div`
  display: flex;
  gap: 16px;
  flex-direction: column;
`;

export const FeaturesSection: React.FC<{ features: FeatureSection[] }> = ({
  features,
}) => {
  return (
    <FeatureListColumn>
      <SectionTitle>Key Features</SectionTitle>
      {features.map((feature) => {
        const FeatureIcon = getIcon(feature.icon);
        const hasBlocks = feature.blocks && feature.blocks.length > 0;

        return (
          <FeatureCard key={feature.id} id={feature.id}>
            <FeatureHeader>
              <FeatureIconBadge>
                <FeatureIcon size={20} weight="bold" />
              </FeatureIconBadge>
              <FeatureTitle>{feature.title}</FeatureTitle>
            </FeatureHeader>
            {feature.description && (
              <FeatureDescription>{feature.description}</FeatureDescription>
            )}

            {hasBlocks ? (
              <BlockRenderer blocks={feature.blocks} />
            ) : (
              <>
                {feature.list && feature.list.length > 0 && (
                  <RenderList items={feature.list} />
                )}
                {feature.table && <RenderTable table={feature.table} />}
                {feature.content.map((text, idx) => (
                  <Paragraph
                    key={idx}
                    dangerouslySetInnerHTML={{
                      __html: text
                        .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
                        .replace(/- /g, "• "),
                    }}
                  />
                ))}
              </>
            )}
          </FeatureCard>
        );
      })}
    </FeatureListColumn>
  );
};

// ============================================================
// TECHNICAL SPECS SECTION
// ============================================================

export const TechnicalSpecsSection: React.FC<{ data: TechnicalSpecsData }> = ({
  data,
}) => {
  return (
    <div>
      <SectionTitle>{data.title}</SectionTitle>
      {data.subsections.map((subsection, idx) => (
        <div key={idx}>
          <SectionSubtitle>{subsection.title}</SectionSubtitle>
          {subsection.description && (
            <Paragraph
              dangerouslySetInnerHTML={{
                __html: subsection.description.replace(
                  /\*\*([^*]+)\*\*/g,
                  "<strong>$1</strong>",
                ),
              }}
            />
          )}
          <RenderTable table={subsection.table} />
          <BlockRenderer blocks={subsection.blocks} />
        </div>
      ))}
    </div>
  );
};

// ============================================================
// HOW IT WORKS SECTION
// ============================================================

const ProcessCard = styled(Card)`
  margin-bottom: 32px;
`;

export const HowItWorksSection: React.FC<{ data: HowItWorksData }> = ({
  data,
}) => {
  return (
    <div>
      <SectionTitle>{data.title}</SectionTitle>
      {data.processes.map((process, idx) => (
        <ProcessCard key={idx}>
          <SectionSubtitle style={{ marginTop: 0 }}>
            {process.title}
          </SectionSubtitle>
          {process.description && <Paragraph>{process.description}</Paragraph>}

          {process.useStepTracker ? (
            <StepTracker
              steps={process.steps.map((step) => ({
                title: step.text,
                icon: "checkCircle",
              }))}
            />
          ) : (
            <RenderList
              items={process.steps.map((step) => ({
                label: step.label,
                text: step.text,
              }))}
              variant={
                process.steps[0]?.type === "numbered" ? "number" : "bullet"
              }
            />
          )}

          <BlockRenderer blocks={process.blocks} />

          {process.note && (
            <Note
              dangerouslySetInnerHTML={{
                __html: process.note.replace(
                  /\*\*([^*]+)\*\*/g,
                  "<strong>$1</strong>",
                ),
              }}
            />
          )}
        </ProcessCard>
      ))}
    </div>
  );
};

// ============================================================
// PRICING SECTION
// ============================================================

export const PricingSection: React.FC<{ data: PricingData }> = ({ data }) => {
  return (
    <div>
      <SectionTitle>{data.title}</SectionTitle>
      <Paragraph
        dangerouslySetInnerHTML={{
          __html: data.currentStatus.replace(
            /\*\*([^*]+)\*\*/g,
            "<strong>$1</strong>",
          ),
        }}
      />
      {data.futureStatus && (
        <Paragraph
          dangerouslySetInnerHTML={{
            __html: data.futureStatus.replace(
              /\*\*([^*]+)\*\*/g,
              "<strong>$1</strong>",
            ),
          }}
        />
      )}
      {data.tiers && data.tiers.length > 0 && (
        <PricingTierGrid tiers={data.tiers} disclaimer={data.disclaimer} />
      )}
    </div>
  );
};

// ============================================================
// SECURITY SECTION
// ============================================================

const SecurityCard = styled(Card)`
  margin-bottom: 24px;
`;

const SecurityTitle = styled.h4`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 16px 0;
`;

const SecurityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SecurityItem = styled.div`
  padding-left: 0;
`;

const SecurityLabel = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const SecurityDescription = styled.span`
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-left: 4px;
`;

export const SecuritySection: React.FC<{ data: SecurityData }> = ({ data }) => {
  return (
    <div>
      <SectionTitle>{data.title}</SectionTitle>
      {data.badges && data.badges.length > 0 && (
        <BadgeRow badges={data.badges} columns={3} />
      )}
      <Grid columns={1}>
        {data.categories.map((category, idx) => (
          <SecurityCard key={idx}>
            <SecurityTitle>{category.title}</SecurityTitle>
            <SecurityList>
              {category.items.map((item, itemIdx) => (
                <SecurityItem key={itemIdx}>
                  <SecurityLabel>{item.label}</SecurityLabel>
                  <SecurityDescription>{item.description}</SecurityDescription>
                </SecurityItem>
              ))}
            </SecurityList>
          </SecurityCard>
        ))}
      </Grid>
    </div>
  );
};

// ============================================================
// USE CASES SECTION
// ============================================================

const UseCaseCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const UseCaseHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const UseCaseTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
`;

const UseCaseDescription = styled.p`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
  line-height: 1.7;
`;

export const UseCasesSection: React.FC<{ useCases: UseCaseSection[] }> = ({
  useCases,
}) => {
  return (
    <div>
      <SectionTitle>Who Should Use This?</SectionTitle>
      <Grid columns={2}>
        {useCases.map((useCase, idx) => {
          const UseCaseIcon = useCase.icon ? getIcon(useCase.icon) : null;
          return (
            <UseCaseCard key={idx}>
              <UseCaseHeader>
                {UseCaseIcon && (
                  <UseCaseIcon
                    size={22}
                    weight="bold"
                    color={theme.colors.primary}
                  />
                )}
                <UseCaseTitle>{useCase.title}</UseCaseTitle>
              </UseCaseHeader>
              <UseCaseDescription>{useCase.description}</UseCaseDescription>
            </UseCaseCard>
          );
        })}
      </Grid>
    </div>
  );
};

// ============================================================
// GETTING STARTED SECTION
// ============================================================

const StepCard = styled(Card)`
  border-left: 4px solid ${({ theme }) => theme.colors.primary};
`;

const StepNumber = styled.div`
  display: inline-block;
  width: 32px;
  height: 32px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border-radius: 50%;
  text-align: center;
  line-height: 32px;
  font-weight: 700;
  margin-bottom: 12px;
`;

const StepTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 12px 0;
`;

export const GettingStartedSection: React.FC<{ data: GettingStartedData }> = ({
  data,
}) => {
  return (
    <div>
      <SectionTitle>{data.title}</SectionTitle>
      <Grid columns={1}>
        {data.steps.map((step, idx) => (
          <StepCard key={idx}>
            <StepNumber>{idx + 1}</StepNumber>
            <StepTitle>{step.title}</StepTitle>
            <RenderList items={step.items.map((text) => ({ text }))} />
          </StepCard>
        ))}
      </Grid>
    </div>
  );
};

// ============================================================
// ROADMAP SECTION
// ============================================================

const RoadmapItem = styled.div`
  display: flex;
  gap: 16px;
  padding: 20px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.primaryBackground};

  &:last-child {
    border-bottom: none;
  }
`;

const RoadmapIcon = styled.div<{ status?: string }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-top: 6px;
  flex-shrink: 0;
  background: ${({ theme, status }) =>
    status === "coming-soon"
      ? theme.colors.secondaryBackground
      : status === "in-progress"
        ? theme.colors.primary
        : theme.colors.textSecondary};
`;

const RoadmapContent = styled.div`
  flex: 1;
`;

const RoadmapLabel = styled.h4`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 4px 0;
`;

const RoadmapDescription = styled.p`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
`;

export const RoadmapSection: React.FC<{ data: RoadmapData }> = ({ data }) => {
  return (
    <div>
      <SectionTitle>{data.title}</SectionTitle>
      <Paragraph>{data.description}</Paragraph>
      <div>
        {data.items.map((item, idx) => (
          <RoadmapItem key={idx}>
            <RoadmapIcon status={item.status} />
            <RoadmapContent>
              <RoadmapLabel>{item.label}</RoadmapLabel>
              <RoadmapDescription>{item.description}</RoadmapDescription>
            </RoadmapContent>
          </RoadmapItem>
        ))}
      </div>
    </div>
  );
};

// ============================================================
// ECOSYSTEM SECTION
// ============================================================

export const EcosystemSection: React.FC<{ data: EcosystemData }> = ({
  data,
}) => {
  return (
    <div>
      <SectionTitle>{data.title}</SectionTitle>
      {data.paragraphs.map((paragraph, idx) => (
        <Paragraph key={idx}>{paragraph}</Paragraph>
      ))}
    </div>
  );
};

// ============================================================
// CTA SECTION
// ============================================================

const CTAContainer = styled.div`
  position: relative;
  text-align: center;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 60px 20px;
  }
`;

const CTAInner = styled.div`
  position: relative;
  z-index: ${({ theme }) => theme.zIndex.root};
`;

const CTADescription = styled.p`
  font-size: 1.125rem;
  margin: 0 0 32px 0;
  opacity: 0.9;
  color: ${({ theme }) => theme.colors.static.white};
`;

const DownloadLinks = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 40px;
`;

const DownloadButton = styled(Button)`
  background: ${({ theme }) => theme.colors.primaryBackground};
  color: ${({ theme }) => theme.colors.primary};
  min-width: 200px;

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    transform: translateY(-2px);
    color: ${({ theme }) => theme.colors.primaryBackground};
  }
`;

const ClosingStatement = styled.p`
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  opacity: 0.95;
  color: ${({ theme }) => theme.colors.static.white};
`;

export const CTASection: React.FC<{ data: CTAData }> = ({ data }) => {
  return (
    <CTAContainer>
      <HeroGradientSection>
        {/* {data.scene3d && <Scene3D block={data.scene3d} showCaption />} */}
        <CTAInner>
          <PrettyTextFade
            color={theme.colors.primaryBackground}
            size={24}
            speedFade={1200}
            speedWave={3600}
            waveStrength={20}
            mode="words"
          >
            {data.title}
          </PrettyTextFade>

          {data.description && (
            <CTADescription>{data.description}</CTADescription>
          )}
          <DownloadLinks>
            {data.downloads.map((download, idx) => (
              <DownloadButton key={idx} href={download.url}>
                {download.platform}
              </DownloadButton>
            ))}
          </DownloadLinks>
          <ClosingStatement>{data.closingStatement}</ClosingStatement>
        </CTAInner>
      </HeroGradientSection>
    </CTAContainer>
  );
};
